import React, { useState } from "react";
import { Header } from "./Header";
import { Footer } from "./Footer";
import { CompletionIndicator } from "./CompletionIndicator";
import { SponsorPanel } from "./SponsorPanel";
import { SponsorCopy, useSponsorPrintMode } from "./SponsorCopy";
import { useApplication } from "./ApplicationProvider";
import { usePublicConfig } from "./PublicConfigProvider";
import { formatIsoDate, isAmendmentsOpen } from "../config";

/**
 * The post-submission screen (status Submitted or Finalized). Reached right
 * after submitting AND on every later return visit through the magic link —
 * the draft persists with that status, so this is simply what the app renders
 * whenever it is loaded.
 *
 * Round 21 adds "Print sponsor copy" here. Step 6 keeps its own copy of that
 * button, deliberately: printing before submitting is still allowed. What Step
 * 6 cannot promise is a reference number — it only exists once submission has
 * run — so this screen is where the sponsor copy is actually useful, and the
 * number is guaranteed present by the very condition that renders this screen.
 * It stays correct across amendments too: amending preserves the reference
 * number rather than reissuing it, and SponsorCopy reads it live from meta.
 *
 * Lifted out of App.tsx (where it was an early return inside AppInner) so that
 * `sponsorPrint` can be a normal piece of local state instead of another hook
 * AppInner must declare above its own early returns.
 */
export function ConfirmationScreen() {
  const { data, update } = useApplication();
  const cfg = usePublicConfig();
  const [sponsorPrint, setSponsorPrint] = useState(false);
  useSponsorPrintMode(sponsorPrint, () => setSponsorPrint(false));

  const finalized = data.meta.status === "Finalized";
  const sponsorDeadline = formatIsoDate(cfg.sponsorDeadline);
  const amendmentsOpen = isAmendmentsOpen(cfg.amendmentsCloseDate);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-4xl w-full mx-auto px-4 py-8">
        {sponsorPrint ? (
          // Replaces the screen entirely rather than hiding it: the print CSS
          // handles chrome, but keeping the rest out of the DOM is what keeps
          // it off the page. Same contract Step 6 has followed since Round 12.
          <SponsorCopy
            onBack={() => setSponsorPrint(false)}
            backLabel="← Back to confirmation"
          />
        ) : (
          <>
            <CompletionIndicator completionPercent={100} status={data.meta.status} />
            <div className="section-card">
              <h2 className="text-2xl font-semibold mb-2">
                {finalized ? "Application finalized" : "Application submitted — one step remains"}
              </h2>
              {data.meta.referenceNumber && (
                <p className="text-sm text-slate-600 mb-2">
                  Reference number: <strong>{data.meta.referenceNumber}</strong>
                </p>
              )}
              {/* Truthful wording: only claim the email was sent when it actually
                  sent; otherwise promise it will follow. */}
              {data.meta.confirmationEmailSent === true ? (
                <p className="text-sm">
                  A confirmation has been sent to <strong>{data.step2.studentEmail}</strong> and{" "}
                  <strong>{data.step2.parentEmail}</strong>.
                </p>
              ) : (
                <p className="text-sm">
                  A confirmation email to <strong>{data.step2.studentEmail}</strong> and{" "}
                  <strong>{data.step2.parentEmail}</strong> will follow.
                </p>
              )}
              {!finalized && (
                <p className="text-sm mt-3">
                  Your application is <strong>submitted</strong>. One step remains:{" "}
                  <strong>
                    secure and confirm a sponsor Post or Unit by {sponsorDeadline}
                  </strong>{" "}
                  using the link emailed to you. Use the panel below when you are ready.
                </p>
              )}
              <p className="text-sm mt-2">
                For final details including session location, visit{" "}
                <a
                  className="underline text-program"
                  href={cfg.programInfoUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  {cfg.programInfoUrl}
                </a>
                .
              </p>

              {/* Round 21 — the reliable place to print the sponsor copy. */}
              <div className="no-print mt-4 border-t border-slate-200 pt-3">
                <p className="text-xs text-slate-500 mb-2">
                  Taking this to a prospective sponsor Post or Unit? Print the sponsor copy — it
                  omits all personal health and contact information and carries your reference
                  number so the Post can verify the application with us.
                </p>
                <button
                  type="button"
                  className="btn-secondary text-sm"
                  onClick={() => setSponsorPrint(true)}
                >
                  Print sponsor copy
                </button>
              </div>

              {amendmentsOpen && (
                <div className="mt-4 border-t border-slate-200 pt-3">
                  <p className="text-xs text-slate-500 mb-2">
                    Need to correct something? You can amend this application until{" "}
                    {formatIsoDate(cfg.amendmentsCloseDate)}. Your reference number stays the same.
                  </p>
                  <button
                    type="button"
                    className="btn-secondary text-sm"
                    onClick={() =>
                      update((d) => {
                        d.meta.status = "AmendmentInProgress";
                        d.meta.currentStep = 6;
                      })
                    }
                  >
                    Make corrections (amend)
                  </button>
                </div>
              )}
            </div>

            <SponsorPanel />
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
